import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Building2, PoundSterling, Receipt, FileSpreadsheet,
  ArrowRight, RefreshCw, Loader2, Users, AlertCircle,
  FileText, TrendingUp, Calendar, ClipboardList, 
  CheckCircle, Clock, AlertTriangle, Banknote, Eye
} from "lucide-react";
import { AllHoursIcon } from "@/components/AllHoursLogo";
import { API } from "@/App";

export const AccountsDashboard = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentStatements, setRecentStatements] = useState([]);
  const [recentReceipts, setRecentReceipts] = useState([]);
  const [arrearsData, setArrearsData] = useState({ arrears: [], total_in_arrears: 0, total_arrears_amount: 0 });
  const [pendingServices, setPendingServices] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, statementsRes, receiptsRes, arrearsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/lettings/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/lettings/statements`, { headers }).catch(() => ({ data: { statements: [] } })),
        axios.get(`${API}/lettings/receipts`, { headers }).catch(() => ({ data: { receipts: [] } })),
        axios.get(`${API}/lettings/arrears`, { headers }).catch(() => ({ data: { arrears: [], total_in_arrears: 0, total_arrears_amount: 0 } })),
        axios.get(`${API}/services/pending-sync`, { headers }).catch(() => ({ data: { services: [] } }))
      ]);

      setStats(statsRes.data);
      setRecentStatements((statementsRes.data.statements || []).slice(0, 5));
      setRecentReceipts((receiptsRes.data.receipts || []).slice(0, 5));
      setArrearsData(arrearsRes.data);
      setPendingServices(servicesRes.data.services?.length || 0);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // Calculate receipt totals
  const totalReceived = recentReceipts.reduce((sum, r) => sum + (r.amount || 0), 0);
  const totalAllocated = recentReceipts.reduce((sum, r) => sum + (r.amount_allocated || 0), 0);
  const allocationRate = totalReceived > 0 ? Math.round((totalAllocated / totalReceived) * 100) : 100;
  
  // Statement totals
  const totalDueToLandlords = recentStatements
    .filter(s => s.status !== 'settled')
    .reduce((sum, s) => sum + (s.summary?.net_payable || 0), 0);
  const settledStatements = recentStatements.filter(s => s.status === 'settled').length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="accounts-dashboard">
      {/* Header */}
      <header className="bg-white border-b sticky top-14 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <AllHoursIcon className="w-10 h-10" />
              <div>
                <h1 className="text-xl font-bold text-slate-800">Accounts Portal</h1>
                <p className="text-xs text-muted-foreground">Financial Management & Rent Allocations</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                {user.name || "Accounts"}
              </Badge>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={fetchData}
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Stats - Enhanced */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/admin/lettings")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.total_properties || stats?.properties || 0}</p>
                  <p className="text-xs text-muted-foreground">Properties</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/admin/lettings/rent")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <PoundSterling className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">£{(stats?.monthly_rent_roll || stats?.total_monthly_rent || 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Monthly Rent Roll</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/admin/lettings/rent")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">£{totalReceived.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Total Received</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/admin/lettings/rent")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Banknote className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">£{totalDueToLandlords.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Due to Landlords</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`hover:shadow-md transition-shadow cursor-pointer ${arrearsData.total_in_arrears > 0 ? 'border-red-200 bg-red-50/30' : ''}`} onClick={() => navigate("/admin/lettings/rent")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${arrearsData.total_in_arrears > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                  <AlertTriangle className={`w-5 h-5 ${arrearsData.total_in_arrears > 0 ? 'text-red-600' : 'text-slate-400'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${arrearsData.total_in_arrears > 0 ? 'text-red-600' : ''}`}>
                    £{arrearsData.total_arrears_amount.toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">{arrearsData.total_in_arrears} in Arrears</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Allocation Progress Card */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-lg">Receipt Allocation Status</h3>
                <p className="text-sm text-muted-foreground">Track how payments are allocated to rent demands</p>
              </div>
              <Badge className={allocationRate === 100 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                {allocationRate}% Allocated
              </Badge>
            </div>
            <Progress value={allocationRate} className="h-3 mb-3" />
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                <span className="font-medium text-foreground">£{totalAllocated.toLocaleString()}</span> allocated
              </span>
              <span className="text-muted-foreground">
                <span className="font-medium text-foreground">£{(totalReceived - totalAllocated).toLocaleString()}</span> unallocated
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Main Actions */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate("/admin/lettings/rent")} data-testid="rent-management-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Receipt className="w-5 h-5 text-emerald-600" />
                Rent & Allocations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Record payments, allocate to demands, generate statements
              </p>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="open-rent-btn">
                Open Rent Management
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate("/accounts/services")}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-purple-600" />
                Landlord Services
                {pendingServices > 0 && (
                  <Badge className="bg-purple-100 text-purple-700 ml-auto">{pendingServices}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Letting fees, inventories, gas safety, boiler service
              </p>
              <Button variant="outline" className="w-full border-purple-300 text-purple-700 hover:bg-purple-50">
                Add Charges
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate("/admin/lettings")}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Property Database
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                View properties, landlords, tenants, and fees
              </p>
              <Button variant="outline" className="w-full">
                View Properties
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate("/accounts/xero")}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-[#13B5EA]" />
                Xero Sync
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Push invoices and statements to Xero
              </p>
              <Button variant="outline" className="w-full border-[#13B5EA] text-[#13B5EA] hover:bg-[#13B5EA] hover:text-white">
                Open Xero Sync
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity - 3 Column Layout */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Recent Receipts */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Receipt className="w-5 h-5 text-green-600" />
                Recent Receipts
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentReceipts.length === 0 ? (
                <p className="text-muted-foreground text-center py-4 text-sm">No receipts recorded yet</p>
              ) : (
                <div className="space-y-3">
                  {recentReceipts.map((receipt) => (
                    <div key={receipt.receipt_id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium">{receipt.reference}</span>
                          {receipt.amount_allocated >= receipt.amount ? (
                            <CheckCircle className="w-3 h-3 text-green-500" />
                          ) : (
                            <Clock className="w-3 h-3 text-amber-500" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {receipt.tenant_name || 'Tenant'} • {new Date(receipt.date_received).toLocaleDateString('en-GB')}
                        </p>
                      </div>
                      <span className="font-medium text-green-600">£{receipt.amount?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
              <Button 
                variant="link" 
                className="w-full mt-2 text-sm"
                onClick={() => navigate("/admin/lettings/rent")}
              >
                View All Receipts
              </Button>
            </CardContent>
          </Card>

          {/* Recent Statements */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Landlord Statements
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentStatements.length === 0 ? (
                <p className="text-muted-foreground text-center py-4 text-sm">No statements generated yet</p>
              ) : (
                <div className="space-y-3">
                  {recentStatements.map((stmt) => (
                    <div key={stmt.statement_id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div>
                        <p className="font-medium text-sm">{stmt.landlord_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {stmt.reference} • {new Date(stmt.statement_date).toLocaleDateString('en-GB')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-emerald-600">£{stmt.summary?.net_payable?.toFixed(2)}</p>
                        <Badge 
                          variant="secondary" 
                          className={`text-xs ${stmt.status === 'settled' ? 'bg-green-100 text-green-700' : stmt.status === 'sent' ? 'bg-blue-100 text-blue-700' : ''}`}
                        >
                          {stmt.status === 'settled' ? 'Settled' : stmt.status === 'sent' ? 'Sent' : 'Draft'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Button 
                variant="link" 
                className="w-full mt-2 text-sm"
                onClick={() => navigate("/admin/lettings/rent")}
              >
                View All Statements
              </Button>
            </CardContent>
          </Card>

          {/* Financial Overview */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                Financial Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-emerald-50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-emerald-700">Monthly Rent Roll</span>
                  </div>
                  <p className="text-xl font-bold text-emerald-600">
                    £{(stats?.monthly_rent_roll || stats?.total_monthly_rent || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-emerald-600/70">
                    {stats?.active_tenants || stats?.total_tenants || 0} active tenants
                  </p>
                </div>

                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-muted-foreground">Management Fees (Est.)</span>
                  </div>
                  <p className="text-lg font-bold">
                    £{Math.round((stats?.monthly_rent_roll || stats?.total_monthly_rent || 0) * 0.10).toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">10% average rate</p>
                </div>

                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">Statements</span>
                  </div>
                  <p className="text-xs text-blue-600">
                    {recentStatements.length} generated • {settledStatements} settled
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Arrears Alert Section */}
        {arrearsData.total_in_arrears > 0 && (
          <Card className="mt-6 border-red-200 bg-red-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                Tenants in Arrears
                <Badge className="bg-red-100 text-red-700 ml-2">{arrearsData.total_in_arrears}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {arrearsData.arrears.slice(0, 6).map((a) => (
                  <div key={a.tenant_id} className="bg-white rounded-lg p-3 border border-red-100">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-sm">{a.tenant_name}</p>
                        <p className="text-xs text-muted-foreground truncate max-w-[180px]">{a.property_address}</p>
                      </div>
                      <span className="font-bold text-red-600">£{a.overdue_amount?.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <Badge variant="destructive" className="text-xs">{a.overdue_demands} periods overdue</Badge>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-6 px-2"
                        onClick={() => navigate("/admin/lettings/rent")}
                      >
                        <Eye className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              {arrearsData.arrears.length > 6 && (
                <Button 
                  variant="link" 
                  className="w-full mt-3 text-red-600"
                  onClick={() => navigate("/admin/lettings/rent")}
                >
                  View All {arrearsData.total_in_arrears} Tenants in Arrears
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default AccountsDashboard;
